import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Wand2, RefreshCcw, CheckCircle2, Clock, Sparkles,
  Heart, ShieldCheck,
} from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { ROOMS, ROOM_LIST, ENERGY } from "../config/homeOsTheme";
import { HomeOSAvatar } from "./HomeOSAvatar";
import { generateMoodAwareCleaningPlan, ZONE_RULES } from "../utils/moodAwareDelegation";

/* ════════════════════════════════════════════════════════════════════════
   DELEGATE CLEANING MODAL — mood-aware fair-balancing.
   Uses moodAwareDelegation util. UI / local state only.
   ════════════════════════════════════════════════════════════════════════ */

export function DelegateCleaningModal({ open, onClose, defaultUseMood = true }) {
  const { cleaningTasks, members, moodCheckins, updateCleaningTask } = useHomeOSState();
  const [roomId, setRoomId] = useState("all");
  const [timeframe, setTimeframe] = useState("today");
  const [includeKids, setIncludeKids] = useState(true);
  const [prioritiseLow, setPrioritiseLow] = useState(false);
  const [avoidOverloaded, setAvoidOverloaded] = useState(true);
  const [useMood, setUseMood] = useState(defaultUseMood);
  const [version, setVersion] = useState(0);

  const planResult = useMemo(() => {
    if (!open) return { assignments: [], byMember: [] };
    return generateMoodAwareCleaningPlan({
      tasks: cleaningTasks,
      members,
      moodCheckins,
      scope: { roomId: roomId === "all" ? null : roomId, timeframe },
      includeKids,
      prioritiseLow,
      avoidOverloaded,
      useMood,
      maxAssignments: 12,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roomId, timeframe, includeKids, prioritiseLow, avoidOverloaded, useMood, cleaningTasks, members, moodCheckins, version]);

  const { assignments, byMember } = planResult;

  const acceptPlan = () => {
    const taskAssignments = assignments.filter(a => a.kind === "task");
    taskAssignments.forEach(p => updateCleaningTask(p.taskId, { assignedTo: p.assignedTo }));
    toast.success(`Assigned ${taskAssignments.length} cleaning tasks fairly${useMood ? ", with mood in mind" : ""}.`);
    onClose();
  };

  if (!open) return null;

  const totalCheckins = (moodCheckins || []).filter(c => (c.createdAt || "").slice(0, 10) === new Date().toISOString().slice(0, 10)).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="delegate-cleaning-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home animate-fadeUp">
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-transparent flex items-center justify-center shadow-sm">
                <Wand2 className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">Fair Cleaning Plan</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">ARA balances jobs by availability, age, energy, workload{useMood ? " and mood." : "."}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 hover:text-gray-600" data-testid="delegate-close">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Mood-aware toggle (prominent) */}
        <div className="px-6 pt-5">
          <label
            className={`flex items-start gap-3 rounded-2xl border-2 px-4 py-3 cursor-pointer transition-all ${
              useMood
                ? "    dark: dark: dark: border-[color:var(--ara-line-soft)] dark:border-rose-800"
                : "bg-gray-50 dark:bg-slate-800/40 border-gray-200 dark:border-slate-700"
            }`}
            data-testid="delegate-mood-toggle-label"
          >
            <div className={`mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${useMood ? "    text-white" : "bg-gray-200 dark:bg-slate-700 text-gray-500"}`}>
              <Heart className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50">Factor in today's mood check-ins</p>
                <input
                  type="checkbox"
                  checked={useMood}
                  onChange={e => setUseMood(e.target.checked)}
                  className="w-5 h-5 accent-rose-500 flex-shrink-0"
                  data-testid="delegate-mood-toggle"
                />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                ARA will adjust cleaning tasks based on how each person is feeling today.
              </p>
              {useMood && (
                <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mt-1" data-testid="delegate-mood-status">
                  {totalCheckins === 0
                    ? "No check-ins yet today — will use normal delegation rules."
                    : `${totalCheckins} check-in${totalCheckins === 1 ? "" : "s"} today.`}
                </p>
              )}
            </div>
          </label>
        </div>

        {/* Controls */}
        <div className="px-6 py-5 space-y-4 border-b border-gray-100 dark:border-slate-800">
          <Control label="Choose room">
            <PillRow
              options={[{ id: "all", label: "All rooms" }, ...ROOM_LIST.map(r => ({ id: r.id, label: r.label }))]}
              value={roomId}
              onChange={setRoomId}
              testIdPrefix="delegate-room"
            />
          </Control>
          <Control label="Timeframe">
            <PillRow
              options={[
                { id: "today",     label: "Today" },
                { id: "thisWeek",  label: "This week" },
                { id: "deepClean", label: "Deep clean day" },
              ]}
              value={timeframe}
              onChange={setTimeframe}
              testIdPrefix="delegate-timeframe"
            />
          </Control>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Toggle label="Include kids" value={includeKids} onChange={setIncludeKids} testId="delegate-kids" />
            <Toggle label="Prioritise low energy" value={prioritiseLow} onChange={setPrioritiseLow} testId="delegate-low" />
            <Toggle label="Avoid overloaded person" value={avoidOverloaded} onChange={setAvoidOverloaded} testId="delegate-overload" />
          </div>
        </div>

        {/* Generated plan */}
        <div className="px-6 py-5">
          {assignments.length === 0 ? (
            <p className="text-base text-gray-500 italic text-center py-8" data-testid="delegate-empty">
              No matching tasks. Try a different room or timeframe.
            </p>
          ) : (
            <>
              {byMember.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="delegate-summary">
                  {byMember.map(m => {
                    const rules = ZONE_RULES[m.moodZone || "none"];
                    return (
                      <div key={m.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border ${rules.badgeCls}`}>
                        <HomeOSAvatar member={m} size="xs" showRing={false} />
                        <span className="text-xs font-extrabold">
                          {m.name}: {m.approvalOnly ? "approve only" : `${m.count}`}
                        </span>
                        {m.moodLabel && <span className="text-[10px] opacity-80">· {m.moodLabel}</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              <ul className="space-y-2" data-testid="delegate-plan-list">
                {assignments.map((p, idx) => {
                  const room = p.room ? ROOMS[p.room] : null;
                  const energyCfg = ENERGY[p.energy] || ENERGY.Low;
                  const rules = ZONE_RULES[p.moodZone || "none"];
                  return (
                    <li
                      key={`${p.taskId}-${idx}`}
                      className={`rounded-2xl border ${room?.tile || (p.kind === "approval" ? "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)]" : "bg-white border-gray-200")} px-4 py-3`}
                      data-testid={`delegate-plan-${idx}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {room && <span className={`text-[10px] font-extrabold uppercase tracking-wider ${room.accent}`}>{room.label}</span>}
                        {p.kind === "approval" && <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />}
                        <p className="text-base font-extrabold text-gray-900 dark:text-gray-50">{p.title}</p>
                        <span className={`ml-auto inline-flex items-center gap-1 text-[10px] font-extrabold ${energyCfg.cls} px-2 py-0.5 rounded-full`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${energyCfg.dot}`} />{p.energy}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-[11px] font-extrabold text-gray-500"><Clock className="w-3 h-3" strokeWidth={3} /> {p.minutes}m</span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-extrabold text-gray-700 dark:text-gray-200">→ {p.assignedName}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${rules.badgeCls}`} data-testid={`delegate-mood-badge-${idx}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${rules.dot}`} />{rules.badge}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-snug">
                        <span className="font-bold">Why:</span> {p.why}
                      </p>
                      {useMood && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug italic">
                          Mood: {p.moodNote}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setVersion(v => v + 1)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl text-sm font-bold bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 hover:border-[color:var(--ara-line-soft)]"
            data-testid="delegate-regenerate"
          >
            <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
            Regenerate
          </button>
          <button
            onClick={() => toast("Plan editor coming soon — tweak ARA preferences in Setup for now.")}
            className="px-3.5 py-2.5 rounded-2xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800"
            data-testid="delegate-edit"
          >
            Edit plan
          </button>
          <button onClick={onClose} className="px-3.5 py-2.5 rounded-2xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="delegate-cancel">
            Cancel
          </button>
          <button
            onClick={acceptPlan}
            disabled={assignments.filter(a => a.kind === "task").length === 0}
            className="ml-auto inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 disabled:opacity-50 text-white font-extrabold text-sm px-5 py-2.5 rounded-2xl shadow-md hover:shadow-none active:scale-[0.97]"
            data-testid="delegate-accept"
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
            Accept plan
          </button>
        </div>

        <div className="px-6 pb-5 -mt-2">
          <p className="text-[11px] text-gray-400 italic flex items-center gap-1">
            <Sparkles className="w-3 h-3" strokeWidth={3} />
            Local plan only. Assigning saves on this device until backend sync lands.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function Control({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function PillRow({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-extrabold border transition-colors ${
              active
                ? "bg-transparent text-white border-cyan-500"
                : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-[color:var(--ara-line-soft)]"
            }`}
            data-testid={`${testIdPrefix}-${o.id}`}
            aria-pressed={active}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-2 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/40 px-3 py-2.5 cursor-pointer">
      <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{label}</span>
      <input
        type="checkbox"
        checked={value}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 accent-cyan-500"
        data-testid={testId}
      />
    </label>
  );
}
